import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, Pencil, Phone } from 'lucide-react';
import { formatMobileWithCountryCode } from '@/features/auth/utils/mobileNumber';
import {
  useTeamCreateFieldsQuery,
  useTeamMemberQuery,
} from '../api/useTeamQueries';
import { AssignedClientsSection } from '../components/AssignedClientsSection';
import {
  buildTeamDetailRows,
  extractTeamMemberData,
  getTeamMemberInitials,
  getTeamMemberName,
  normalizeTeamCreateFields,
} from '../utils/teamUtils';

function DetailItem({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="rounded-xl bg-[var(--dash-soft)] p-3.5 text-left">
      <div className="mb-1 text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
        {label}
      </div>
      <p className="m-0 text-sm font-medium break-words text-[var(--dash-ink)]">
        {value?.trim() ? value : '—'}
      </p>
    </div>
  );
}

export default function TeamMemberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const memberQ = useTeamMemberQuery(id);
  const createFieldsQ = useTeamCreateFieldsQuery();

  const member = useMemo(
    () => extractTeamMemberData(memberQ.data),
    [memberQ.data],
  );

  const selectedFields = useMemo(
    () => normalizeTeamCreateFields(createFieldsQ.data),
    [createFieldsQ.data],
  );

  const detailRows = useMemo(
    () =>
      buildTeamDetailRows(selectedFields, member, {
        mobileCountryCode: String(member.mobileCountryCode || ''),
      }),
    [selectedFields, member],
  );

  const name = getTeamMemberName(member);
  const email = detailRows.find((r) => r.key === 'email')?.value || '';
  const phone =
    detailRows.find((r) => r.key === 'mobileNumber')?.value ||
    formatMobileWithCountryCode(
      member.mobileNumber as string | undefined,
      member.mobileCountryCode as string | undefined,
    );
  const isLoading = memberQ.isPending || createFieldsQ.isPending;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5">
      <button
        type="button"
        onClick={() => navigate('/team')}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-[var(--dash-muted)] transition hover:text-[var(--dash-ink)]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to team
      </button>

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--dash-brand)]" />
        </div>
      ) : (
        <div className="camp-fade-up flex flex-col gap-5">
          <div className="relative overflow-hidden rounded-2xl">
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(135deg, #145E8C 0%, #007A78 40%, #33A65B 100%)',
              }}
            />
            <div className="relative z-10 flex flex-col items-center gap-5 px-6 py-7 text-white sm:flex-row sm:gap-6 sm:px-8 sm:py-8">
              <div className="flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-full border-[3px] border-white/70 bg-white/20 text-2xl font-bold backdrop-blur-sm">
                {getTeamMemberInitials(member)}
              </div>
              <div className="flex flex-1 flex-col items-center gap-1.5 text-center sm:items-start sm:text-left">
                <h1 className="m-0 text-[22px] font-bold leading-tight">{name}</h1>
                <span className="rounded-full border border-white/25 bg-white/10 px-3 py-[3px] text-[10px] font-bold tracking-widest uppercase backdrop-blur-sm">
                  Sub-account
                </span>
                <div className="mt-1 flex flex-wrap items-center justify-center gap-4 text-[13px] opacity-90 sm:justify-start">
                  {email ? (
                    <span className="flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 opacity-70" />
                      {email}
                    </span>
                  ) : null}
                  {phone ? (
                    <span className="flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5 opacity-70" />
                      {phone}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap justify-center gap-2">
                <button
                  type="button"
                  onClick={() => id && navigate(`/team/${id}/edit`)}
                  className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-white/90 px-4 text-xs font-bold tracking-wide text-[var(--dash-brand)] uppercase transition hover:bg-white"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
              </div>
            </div>
          </div>

          <section className="camp-section">
            <header className="mb-4 flex items-center gap-2 border-b border-[var(--dash-line)] pb-3 text-left">
              <h2 className="m-0 text-base font-semibold text-[var(--dash-ink)]">
                Profile Details
              </h2>
            </header>
            {createFieldsQ.isError ? (
              <p className="m-0 text-sm text-red-600">
                Could not load profile fields.
              </p>
            ) : detailRows.length === 0 ? (
              <p className="m-0 text-sm text-[var(--dash-muted)]">
                No profile fields are configured for team members.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {detailRows.map((row) => (
                  <DetailItem key={row.key} label={row.label} value={row.value} />
                ))}
              </div>
            )}
          </section>

          {id ? <AssignedClientsSection memberId={id} /> : null}
        </div>
      )}
    </div>
  );
}
